import { inject, observer } from 'mobx-react';
import React, { Component } from 'react';
import styled from 'styled-components';

import { ImpuestosAguaCuentasRecord, TableColumns } from '../../models';
import { ImpuestosAguaMedidorStore } from '../../stores';

import { ColumnProps } from 'antd/lib/table';
import { Table, TableNumberCell, TextBlock, TotalTable } from '../shared';
import { SubformSaveButton } from '../shared/SubformSaveButton';

interface CuentasSubformProps {
    isReadonly: boolean;
    impuestosAguaCuentasStore?: ImpuestosAguaMedidorStore;
}

const Header = styled(Table)`
    .ant-table-placeholder {
        display: none;
    }
`;

@inject('impuestosAguaCuentasStore')
@observer
export class CuentasSubform extends Component<CuentasSubformProps> {
    readonly cuentasHeaderColumns: Array<ColumnProps<{}>> = [
        {
            title: 'CONCEPTO',
            key: 'concepto',
            align: 'center'
        },
        {
            title: (
                <TextBlock text="N° DE USUARIOS Y N° DE TOMAS EXISTENTES EN EL PADRÓN AL ÚLTIMO DÍA DEL MES\n(A)" />
            ),
            key: 'cuentas',
            children: ['USUARIOS', 'TOMAS'].map(key => ({
                title: <TextBlock text={`N° DE\\n${key}`} />,
                key: `cuentas${key}`,
                width: 150
            }))
        },
        {
            title: (
                <TextBlock text="N° DE USUARIOS Y N° DE TOMAS QUE REALIZARON PAGOS DEL 1 DE ENERO AL ÚLTIMO DÍA DEL MES\n(B)" />
            ),
            key: 'cuentasAnterior',
            children: ['USUARIOS', 'TOMAS'].map(key => ({
                title: <TextBlock text={`N° DE\\n${key}`} />,
                key: `cuentasAnterior${key}`,
                width: 150
            }))
        }
    ];

    readonly cuentasMedidoColumnsByKind: Record<
        'ConMedidor' | 'SinMedidor' | 'TOTAL',
        TableColumns<ImpuestosAguaCuentasRecord>
    > = ['ConMedidor', 'SinMedidor', 'TOTAL'].reduce(
        (result, kind) => ({
            ...result,
            [kind]: [
                {
                    dataIndex: 'concepto'
                },
                {
                    children: [
                        this.generateCuentasNumberColumn(
                            `numeroDeCuentasEjercicio${kind}`,
                            'usuario'
                        ),
                        this.generateCuentasNumberColumn(
                            `numeroDeCuentasEjercicio${kind}`,
                            'toma',
                            undefined,
                            true
                        )
                    ]
                },
                {
                    children: [
                        this.generateCuentasNumberColumn(
                            `numeroDeCuentasEjercicioAnterior${kind}`,
                            'usuario'
                        ),
                        this.generateCuentasNumberColumn(
                            `numeroDeCuentasEjercicioAnterior${kind}`,
                            'toma',
                            undefined,
                            true
                        )
                    ]
                }
            ]
        }),
        {} as Record<
            'ConMedidor' | 'SinMedidor' | 'TOTAL',
            TableColumns<ImpuestosAguaCuentasRecord>
        >
    );

    generateCuentasDePagosColumns = (ejercicio: number): TableColumns<any> => [
        {
            title: (
                <TextBlock
                    text={` N° DE USUARIOS Y N° DE TOMAS QUE REALIZARON ÚNICAMENTE PAGOS DE ${ejercicio}\\n(C)`}
                />
            ),
            dataIndex: 'numeroCuentasPagosEjercicio',
            children: [
                this.generateCuentasNumberColumn('numeroCuentasPagosEjercicio', 'usuario', '(C1)'),
                this.generateCuentasNumberColumn(
                    'numeroCuentasPagosEjercicio',
                    'toma',
                    '(C2)',
                    true
                )
            ]
        },
        {
            title: (
                <TextBlock
                    text={`N° DE USUARIOS Y N° DE TOMAS QUE REALIZARON ÚNICAMENTE PAGOS DE EJERCICIOS ANTERIORES A ${ejercicio}\\n(D)`}
                />
            ),
            dataIndex: 'numeroCuentasPagosEjercicioAnterior',
            children: [
                this.generateCuentasNumberColumn(
                    'numeroCuentasPagosEjercicioAnterior',
                    'usuario',
                    '(D1)'
                ),
                this.generateCuentasNumberColumn(
                    'numeroCuentasPagosEjercicioAnterior',
                    'toma',
                    '(D2)',
                    true
                )
            ]
        },
        {
            title: (
                <TextBlock
                    text={`N° DE USUARIOS Y N° DE TOMAS QUE REALIZARON TANTO PAGOS DE ${ejercicio} COMO DE EJERCICIOS ANTERIORES A ${ejercicio}\\n(E)`}
                />
            ),
            dataIndex: 'numeroCuentasPagosEjercicioMixto',
            children: [
                this.generateCuentasNumberColumn(
                    'numeroCuentasPagosEjercicioMixto',
                    'usuario',
                    '(E1)'
                ),
                this.generateCuentasNumberColumn(
                    'numeroCuentasPagosEjercicioMixto',
                    'toma',
                    '(E2)',
                    true
                )
            ]
        },
        {
            title: (
                <TextBlock
                    text={`N° DE USUARIOS Y N° DE TOMAS QUE REALIZARON PAGOS DEL 1 DE ENERO AL ÚLTIMO DÍA DEL MES\n(F=C+D+E)`}
                />
            ),
            dataIndex: 'pagosTotal',
            children: [
                this.generateCuentasNumberColumn('pagosTotal', 'usuario', '(F1=C1+D1+E1)', true),
                this.generateCuentasNumberColumn('pagosTotal', 'toma', '(F2=C2+D2+E2)', true)
            ]
        }
    ];

    generateCuentasNumberColumn(
        key: string,
        kind: 'toma' | 'usuario',
        subtitle?: string,
        isReadonlyCell: boolean = false
    ): ColumnProps<ImpuestosAguaCuentasRecord> {
        return {
            title: <TextBlock text={`N° DE\\n${kind.toUpperCase()}S\\n${subtitle || ''}`} />,
            dataIndex: `${key}.${kind}`,
            align: 'right',
            width: '12.5%',
            render: (_, record) =>
                this.renderCuentas(
                    record.key === 'cuentas' ? `${key}.${kind}` : `${key}.${record.key}.${kind}`,
                    (record as any)[`${key}.${kind}`],
                    isReadonlyCell
                )
        };
    }

    renderConMedidorTitle = () => 'USO DE LA BASE CON MEDIDOR';

    renderSinMedidorTitle = () => 'USO DE LA BASE SIN MEDIDOR';

    renderCuentas = (path: string, value: number, isReadonlyCell: boolean) => {
        const { isReadonly, impuestosAguaCuentasStore } = this.props;
        const { setCuentasValue } = impuestosAguaCuentasStore!;
        const isTotal = path.toLocaleLowerCase().includes('total');

        return (
            <TableNumberCell
                type={'integer'}
                isReadonly={isReadonly || isReadonlyCell || isTotal}
                path={path}
                value={value}
                onChange={setCuentasValue}
            />
        );
    };

    render() {
        const { isReadonly, impuestosAguaCuentasStore } = this.props;
        const {
            ejercicio,
            cuentasConMediadorRecords,
            cuentasSinMediadorRecords,
            cuentasTotalRecords,
            CuentasPagos,
            isSaving,
            isLoading,
            saveMedidor
        } = impuestosAguaCuentasStore!;

        const {
            cuentasHeaderColumns,
            cuentasMedidoColumnsByKind,
            generateCuentasDePagosColumns
        } = this;

        const cuentasConMediadorColumns = cuentasMedidoColumnsByKind.ConMedidor;
        cuentasConMediadorColumns[0].render = (text, record) => ({
            props: {
                className:
                    record.key === 'ConMedidortotal' || record.key === 'SinMedidortotal'
                        ? 'total-cell'
                        : undefined
            },
            children: text
        });

        const cuentasSinMediadorColumns = cuentasMedidoColumnsByKind.SinMedidor;
        cuentasSinMediadorColumns[0].render = (text, record) => ({
            props: {
                className:
                    record.key === 'ConMedidortotal' || record.key === 'SinMedidortotal'
                        ? 'total-cell'
                        : undefined
            },
            children: text
        });

        const [_, ...cuentasTotalValueColumns] = cuentasMedidoColumnsByKind.TOTAL;
        const cuentasDePagosColumns = generateCuentasDePagosColumns(ejercicio);

        return (
            <>
                <Header columns={cuentasHeaderColumns} />
                <Table
                    title={this.renderConMedidorTitle}
                    columns={cuentasConMediadorColumns}
                    dataSource={cuentasConMediadorRecords}
                    showHeader={false}
                    loading={isLoading}
                />
                <Table
                    title={this.renderSinMedidorTitle}
                    columns={cuentasSinMediadorColumns}
                    dataSource={cuentasSinMediadorRecords}
                    showHeader={false}
                    loading={isLoading}
                />
                <TotalTable
                    valueColumns={cuentasTotalValueColumns}
                    dataSource={cuentasTotalRecords}
                    showHeader={false}
                    loading={isLoading}
                />
                <Table
                    dataSource={CuentasPagos}
                    columns={cuentasDePagosColumns}
                    loading={isLoading}
                />

                {!isReadonly && <SubformSaveButton isSaving={isSaving} onSave={saveMedidor} />}
            </>
        );
    }
}
